/* WhatsApp odesílač (Level 1 — wa.me): 1 klik otevře WhatsApp s předvyplněnou zprávou na klienta.
 * Prodejní šablony (před/po schůzce, servis, doporučení, nabídka, reaktivace…) + chytrý návrh
 * podle fáze a dat klienta. Text je editovatelný. Zdarma, bez API.
 *
 * REŽIM „jedna hotová zpráva" (prop presetText):
 * Když volající ví, PROČ píše (servisní radar zná konkrétní smlouvu a termín),
 * nemá poradce co vybírat — dostane hotovou zprávu a jen ji přečte/upraví.
 * Majitel 8/2026: „A ne, že když dám WhatsApp, tak je tam víc možností, jak mu
 * mám napsat. To nedává smysl." Bez presetText zůstává původní chování se
 * šablonami (klientská karta, radar příležitostí). */
import { useMemo, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X, MessageCircle, Info, Star, ExternalLink,
  CalendarPlus, CalendarCheck, AlarmClock, Send, Wrench, HeartHandshake, Calculator, RefreshCw, Cake, Contact,
} from "lucide-react";
import { contactFullName } from "@/lib/contactName";
import { whatsappOdkaz } from "@/lib/crm/zpravaDoporuceni";
import { cisloProVytaceni } from "@/lib/telefonCislo";
import { osloveniProZpravu, firstName } from "@/lib/czechNames";
import { useLanguage } from "@/contexts/LanguageContext";

/* Číslo pro wa.me řeší lib/telefonCislo (jeden zdroj pravdy). Zdejší kopie
   odvozovala předvolbu z JAZYKA APPKY: český poradce, který si ji přepnul do
   slovenštiny, psal na +421 — tedy někomu úplně jinému. Teď rozhoduje ZEMĚ
   PORADCE a číslo, které předvolbu už má, se nechává být. */

/* šablony — {jmeno} {poradce} {rezervace} {vizitka} se nahradí. labelSk/textSk = slovenská verze. */
const SABLONY = [
  { id: "domluva", ikona: CalendarPlus, label: "Domluvit schůzku", labelSk: "Dohodnúť stretnutie", stages: ["lead", "new", "calling", "volani"],
    text: "Dobrý den {jmeno}, díky za čas! 🙂 Posílám odkaz, kde si rovnou vyberete termín naší schůzky, ať se nemusíme domlouvat sem a tam: {rezervace}\nTěším se! {poradce}",
    textSk: "Dobrý deň {jmeno}, ďakujem za čas! 🙂 Posielam odkaz, kde si rovno vyberiete termín nášho stretnutia, nech sa nemusíme dohadovať sem a tam: {rezervace}\nTeším sa! {poradce}" },
  { id: "potvrzeni", ikona: CalendarCheck, label: "Potvrzení rezervace", labelSk: "Potvrdenie rezervácie", stages: ["a1", "predstaveni"],
    text: "Dobrý den {jmeno}, potvrzuji naši schůzku — moc se těším! Kdyby se cokoliv změnilo, stačí napsat. {poradce}",
    textSk: "Dobrý deň {jmeno}, potvrdzujem naše stretnutie — veľmi sa teším! Keby sa čokoľvek zmenilo, stačí napísať. {poradce}" },
  { id: "pripominka", ikona: AlarmClock, label: "Připomínka (den před)", labelSk: "Pripomienka (deň pred)", stages: ["a1", "a2", "analyza"],
    text: "Dobrý den {jmeno}, jen krátká připomínka — zítra se vidíme. 👍 Vezměte si prosím přehled stávajících smluv, ať Vám dokážu nejlíp poradit. Budu se těšit! {poradce}",
    textSk: "Dobrý deň {jmeno}, len krátka pripomienka — zajtra sa vidíme. 👍 Vezmite si prosím prehľad existujúcich zmlúv, nech Vám viem najlepšie poradiť. Budem sa tešiť! {poradce}" },
  { id: "po_schuzce", ikona: Send, label: "Po schůzce + materiály", labelSk: "Po stretnutí + materiály", stages: ["a2", "fin_plan", "plan", "prezentace"],
    text: "Dobrý den {jmeno}, moc děkuji za dnešní schůzku, byla radost! 🙏 Posílám slíbené materiály a Váš plán. Kdyby cokoliv, jsem Vám plně k dispozici. {poradce}",
    textSk: "Dobrý deň {jmeno}, veľmi ďakujem za dnešné stretnutie, bola radosť! 🙏 Posielam sľúbené materiály a Váš plán. Keby čokoľvek, som Vám plne k dispozícii. {poradce}" },
  { id: "servis", ikona: Wrench, label: "Servisní schůzka (online)", labelSk: "Servisné stretnutie (online)", stages: ["servis", "signed", "podpis", "cerpani"],
    text: "Dobrý den {jmeno}, ozývám se kvůli pravidelnému servisu Vašich smluv — ať máte jistotu, že vše stále sedí a nikde zbytečně neplatíte. Klidně online, zabere to ~20 minut: {rezervace}\n{poradce}",
    textSk: "Dobrý deň {jmeno}, ozývam sa kvôli pravidelnému servisu Vašich zmlúv — nech máte istotu, že všetko stále sedí a nikde zbytočne neplatíte. Pokojne online, zaberie to ~20 minút: {rezervace}\n{poradce}" },
  { id: "doporuceni", ikona: HeartHandshake, label: "Žádost o doporučení", labelSk: "Žiadosť o odporúčanie", stages: ["servis", "signed", "podpis"],
    text: "Dobrý den {jmeno}, moc si vážím naší spolupráce. 🙏 Kdybyste znal/a někoho ve svém okolí, komu by se hodila stejná péče o finance jako Vám, budu vděčný za doporučení — hodně to pro mě znamená. Díky moc! {poradce}",
    textSk: "Dobrý deň {jmeno}, veľmi si vážim našu spoluprácu. 🙏 Keby ste poznal/a niekoho vo svojom okolí, komu by sa hodila rovnaká starostlivosť o financie ako Vám, budem vďačný za odporúčanie — veľa to pre mňa znamená. Ďakujem pekne! {poradce}" },
  { id: "nabidka", ikona: Calculator, label: "Poslat nabídku / propočet", labelSk: "Poslať ponuku / prepočet", stages: [],
    text: "Dobrý den {jmeno}, mám pro Vás konkrétní propočet, který Vám může reálně pomoct (ušetřit i vydělat). Mrkněte: {vizitka}\nRád to s Vámi proberu. {poradce}",
    textSk: "Dobrý deň {jmeno}, mám pre Vás konkrétny prepočet, ktorý Vám môže reálne pomôcť (ušetriť aj zarobiť). Pozrite: {vizitka}\nRád to s Vami preberiem. {poradce}" },
  { id: "reaktivace", ikona: RefreshCw, label: "Reaktivace (dlouho ticho)", labelSk: "Reaktivácia (dlho ticho)", stages: ["lead"],
    text: "Dobrý den {jmeno}, dlouho jsme se neslyšeli — jak se máte? 🙂 Sazby i trhy se teď dost hýbou, rád bych s Vámi nezávazně prošel, jestli Vám finanční plán pořád sedí. Co třeba krátký hovor tento týden? {poradce}",
    textSk: "Dobrý deň {jmeno}, dlho sme sa nepočuli — ako sa máte? 🙂 Sadzby aj trhy sa teraz dosť hýbu, rád by som s Vami nezáväzne prešiel, či Vám finančný plán stále sedí. Čo tak krátky hovor tento týždeň? {poradce}" },
  { id: "narozeniny", ikona: Cake, label: "Přání k narozeninám", labelSk: "Blahoželanie k narodeninám", stages: [],
    text: "Dobrý den {jmeno}, všechno nejlepší k narozeninám! 🎉 Přeji hlavně pevné zdraví a ať se daří. {poradce}",
    textSk: "Dobrý deň {jmeno}, všetko najlepšie k narodeninám! 🎉 Prajem hlavne pevné zdravie a nech sa darí. {poradce}" },
  { id: "vizitka", ikona: Contact, label: "Poslat vizitku", labelSk: "Poslať vizitku", stages: [],
    text: "Dobrý den {jmeno}, posílám na sebe kontakt, ať mě máte po ruce: {vizitka}\nKdyby cokoliv ohledně financí, klidně napište. {poradce}",
    textSk: "Dobrý deň {jmeno}, posielam na seba kontakt, nech ma máte poruke: {vizitka}\nKeby čokoľvek ohľadom financií, pokojne napíšte. {poradce}" },
];

/* Doplní tokeny šablony.
 *
 * {jmeno} se polyká i s mezerou PŘED sebou: šablony mají tvar „Dobrý den {jmeno},"
 * a když oslovení nemáme (chybí jméno, nebo si engine tvarem není jistý), musí
 * vzniknout „Dobrý den," — ne „Dobrý den ,". */
function fill(text, ctx) {
  const jmeno = String(ctx.jmeno || "").trim();
  /* Odkazy jsou v šablonách vždy ve tvaru [text dvojtecka mezera placeholder].
     Když odkaz nemáme (poradce nemá vizitkový slug, takže žádná veřejná stránka
     neexistuje), musí zmizet i ta dvojtečka — jinak věta končí do prázdna.
     Dřív se místo toho posílal odkaz na kořen aplikace, tedy klientovi
     PŘIHLAŠOVACÍ OBRAZOVKA. */
  const odkaz = (hodnota) => (String(hodnota || "").trim() ? `: ${hodnota}` : "");
  return text
    .replace(/ ?\{jmeno\}/g, jmeno ? ` ${jmeno}` : "")
    .replace(/\{poradce\}/g, ctx.poradce || "")
    .replace(/:? ?\{rezervace\}/g, odkaz(ctx.rezervace))
    .replace(/:? ?\{vizitka\}/g, odkaz(ctx.vizitka));
}

/* chytrý návrh dle fáze + dat */
function suggest(contact) {
  const ids = [];
  const stage = (contact.stage || "lead").toLowerCase();
  // narozeniny dnes?
  if (contact.dob) {
    try { const d = new Date(contact.dob); const n = new Date(); if (d.getDate() === n.getDate() && d.getMonth() === n.getMonth()) ids.push("narozeniny"); } catch { /* ignore */ }
  }
  SABLONY.forEach((s) => { if (s.stages.includes(stage)) ids.push(s.id); });
  if (ids.length === 0) ids.push("domluva", "nabidka");
  return [...new Set(ids)];
}

export default function WhatsAppSender({ contact, advisorName = "", advisorSlug = "", open, onClose, presetText = "", presetLabel = "" }) {
  const { t, lang } = useLanguage();
  const ctx = useMemo(() => {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    return {
      /* OSLOVENÍ = 5. pád (Petr → Petře), pro SK nominativ. Když si engine
         tvarem není jistý, vrátí "" a šablona jméno vynechá — viz fill(). */
      jmeno: osloveniProZpravu(contact?.name, lang),
      /* PODPIS poradce zůstává v 1. pádě — je to jeho jméno pod zprávou, ne
         oslovení. firstName() (ne split) kvůli vietnamským jménům: u
         „Nguyen Duc Trung" je první slovo příjmení, podepsat se má „Trung". */
      poradce: firstName(advisorName) || advisorName || "",
      /* REZERVACE = rezervační stránka /book/:slug, kde si klient vybere termín
         (App.js: <Route path="/book/:slug" />). Doteď mířila na /v/:slug, tedy
         na vizitku — a šablona přitom klientovi slibovala výběr termínu. Když
         poradce na vizitce rezervační widget nemá zapnutý, klient nenašel nic. */
      rezervace: advisorSlug ? `${origin}/book/${advisorSlug}` : "",
      /* VIZITKA = veřejný profil poradce. Bez slugu žádný neexistuje; prázdná
         hodnota vypadne i s dvojtečkou (viz fill), místo odkazu na login. */
      vizitka: advisorSlug ? `${origin}/v/${advisorSlug}` : "",
    };
  }, [contact, advisorName, advisorSlug, lang]);

  const tplLabel = (s) => (lang === "sk" ? s.labelSk || s.label : s.label);
  const tplText = (s) => fill((lang === "sk" ? s.textSk || s.text : s.text) || "", ctx);

  const suggestedIds = useMemo(() => suggest(contact || {}), [contact]);
  const ordered = useMemo(() => [...SABLONY].sort((a, b) => (suggestedIds.includes(b.id) ? 1 : 0) - (suggestedIds.includes(a.id) ? 1 : 0)), [suggestedIds]);

  const hotovaZprava = !!String(presetText || "").trim();   // režim „jedna hotová zpráva"
  const [sel, setSel] = useState(ordered[0]?.id);
  const [text, setText] = useState("");
  useEffect(() => {
    if (!open) return;
    if (hotovaZprava) { setSel(null); setText(presetText); return; }
    const s = ordered[0];
    setSel(s?.id);
    setText(tplText(s || {}));
  }, [open, lang, presetText]); // eslint-disable-line

  const choose = (s) => { setSel(s.id); setText(tplText(s)); };
  const num = cisloProVytaceni(contact?.phone);
  const send = () => {
    if (!num) return;
    // whatsappOdkaz = jeden zdroj pravdy pro wa.me odkaz (viz lib/crm/zpravaDoporuceni.js).
    window.open(whatsappOdkaz(num, text), "_blank", "noopener");
    onClose?.();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}
          className="fixed inset-0 z-[140] flex items-end sm:items-center justify-center p-0 sm:p-4" style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(6px)" }} data-testid="wa-sender">
          <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0 }} onClick={(e) => e.stopPropagation()}
            className="w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl overflow-hidden" style={{ background: "var(--surface,#fff)", border: "1px solid var(--line)", maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
            <div className="flex items-center justify-between px-4 py-3" style={{ background: "#25D366", color: "#fff" }}>
              <div className="flex items-center gap-2 font-bold"><MessageCircle size={18} /> WhatsApp · {contactFullName(contact) || "klient"}</div>
              <button onClick={onClose} aria-label={t("auto.spolecne.zavrit")}><X size={18} /></button>
            </div>

            {!num && <div className="px-4 py-3 text-sm" style={{ color: "var(--negative-text)" }}>{t("auto.whatsAppSender.klientNemaTelefonDopln")}</div>}

            <div className="p-4 overflow-y-auto" style={{ flex: 1 }}>
              {/* Hotová zpráva → žádný výběr šablon. Jen řádek, PROČ se píše (z dat radaru). */}
              {hotovaZprava ? (
                presetLabel ? (
                  <div className="text-drobny mb-3 flex items-start gap-1.5 rounded-xl px-3 py-2" style={{ background: "var(--surface-2,#efeae0)", color: "var(--ink)", border: "1px solid var(--line)" }} data-testid="wa-preset-label">
                    <Info size={12} style={{ color: "#25D366", flexShrink: 0, marginTop: 2 }} />
                    <span>{presetLabel}</span>
                  </div>
                ) : null
              ) : (
                <>
                  <div className="text-popisek uppercase tracking-wider font-bold mb-2" style={{ color: "var(--muted)" }}>{t("auto.whatsAppSender.sablona")}</div>
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {ordered.map((s) => {
                      const sug = suggestedIds.includes(s.id);
                      const active = sel === s.id;
                      return (
                        <button key={s.id} onClick={() => choose(s)} data-testid={`wa-tpl-${s.id}`}
                          className="px-2.5 py-1.5 rounded-full text-xs font-bold inline-flex items-center gap-1"
                          style={active ? { background: "#25D366", color: "#fff" } : { background: "var(--surface-2,#efeae0)", color: "var(--ink)", border: "1px solid var(--line)" }}>
                          {s.ikona && <s.ikona size={12} strokeWidth={2.2} aria-hidden="true" style={{ flexShrink: 0 }} />}{tplLabel(s)}{sug && !active && <Star size={11} style={{ color: "#25D366" }} />}
                        </button>
                      );
                    })}
                  </div>
                  {suggestedIds.length > 0 && <div className="text-drobny mb-3 flex items-center gap-1" style={{ color: "var(--muted)" }}><Star size={11} style={{ color: "#25D366" }} /> {t("auto.whatsAppSender.navrhDleFazeKlienta")} ({(contact?.stage || "lead")})</div>}
                </>
              )}

              <div className="text-popisek uppercase tracking-wider font-bold mb-1" style={{ color: "var(--muted)" }}>{t("auto.whatsAppSender.zpravaUpravPodlePotreby")}</div>
              <textarea value={text} onChange={(e) => setText(e.target.value)} rows={hotovaZprava ? 13 : 7} data-testid="wa-text"
                style={{ width: "100%", background: "var(--surface-2,#efeae0)", border: "1px solid var(--line)", borderRadius: 12, padding: "10px 12px", fontSize: 14, color: "var(--ink)", outline: "none", resize: "vertical", fontFamily: "inherit" }} />
              <div className="text-drobny mt-1" style={{ color: "var(--muted)" }}>{t("auto.whatsAppSender.otevreWhatsappSTimto")}</div>
            </div>

            <div className="p-3 flex items-center gap-2" style={{ borderTop: "1px solid var(--line)" }}>
              <button onClick={onClose} className="px-4 py-2.5 rounded-xl text-sm font-bold" style={{ background: "var(--surface-2,#efeae0)", color: "var(--ink)" }}>{t("auto.whatsAppSender.zrusit")}</button>
              <button onClick={send} disabled={!num || !text.trim()} data-testid="wa-send"
                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-bold inline-flex items-center justify-center gap-2 disabled:opacity-40" style={{ background: "#25D366", color: "#fff" }}>
                <MessageCircle size={16} /> {t("auto.whatsAppSender.otevritWhatsapp")} <ExternalLink size={13} />
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
